import React from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { withGlobalAlertBanner } from '@hocs/withGlobalAlertBanner.hoc.tsx';
import { withLoading } from '@hocs/withLoading.hoc.tsx';
import SplitScreenLayout from '@layouts/auth/split-screen-layout';
import MainLayout from '@layouts/main/layout';
import { ROUTES } from '@router/constants.ts';
import RequireAuth from '@router/requireAuth.tsx';
import { getRandomAuthImage } from '@routes/auth/___shared___/helpers.ts';
import AccountLocked from '@routes/auth/account-locked/page.tsx';
import Login from '@routes/auth/login/page.tsx';
import PasswordChange from '@routes/auth/password-change/page.tsx';
import PasswordChangeSuccess from '@routes/auth/password-change-success/page.tsx';
import Register from '@routes/auth/register/page.tsx';
import MyResults from '@routes/profile-bar/___pages___/my-results/page.tsx';
import { CertificatePreview } from '@routes/sidebar/admin-center/creator/___pages___/pathways/___pages___/certificate-preview/page.tsx';
import Home from '@routes/sidebar/home/page.tsx';
import { TaskEvaluationLog } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/evaluation-log';
import { PrintEvaluationLog } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-evaluation-log';
import { PrintTask } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-task';
import MyLearning from '@routes/sidebar/learning/page.tsx';
import Notifications from '@routes/sidebar/notifications/page.tsx';
import UserSettings from '@routes/sidebar/settings/page.tsx';
import { AdminLandingGuard } from '@routes/system/___shared___/components/AdminLandingGuard.tsx';
import { BlueByteAdminRoleGuard } from '@routes/system/___shared___/components/BlueByteAdminRoleGuard.tsx';
import HealthCheckGuard from '@routes/system/___shared___/components/HealthCheckGuard.tsx';
import ServiceUnavailable from '@routes/system/maintenance/page.tsx';
import NoSuchPage from '@routes/system/no-page/page.tsx';
import NoClient from '@routes/system/not-found/page.tsx';
import InternalServerError from '@routes/system/server-error/page.tsx';
import NotAuthorized from '@routes/system/unauthorized/page.tsx';

const MyProfilePage = React.lazy(() => import('@routes/profile-bar/___pages___/my-profile/page.tsx'));
const MyProfile = withLoading(MyProfilePage);

const LearningOverviewPage = React.lazy(
    () => import('@routes/sidebar/learning/___pages___/learning-overview/page.tsx'),
);
const LearningOverview = withLoading(LearningOverviewPage);

const VesselCrewMemberPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/___shared___/dashboards/___pages___/vessel-crew-member/page.tsx').then(
        ({ VesselCrewMember }) => ({
            default: VesselCrewMember,
        }),
    ),
);
const VesselCrewMember = withLoading(VesselCrewMemberPage);

const CreateElementPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/elements/___pages___/create-element/page.tsx').then(
        ({ CreateElement }) => ({
            default: CreateElement,
        }),
    ),
);
const CreateElement = withLoading(CreateElementPage);

const EditElementPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/elements/___pages___/edit-element/page.tsx').then(
        ({ EditElement }) => ({
            default: EditElement,
        }),
    ),
);
const EditElement = withLoading(EditElementPage);

const CreatorElementsPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/elements/page.tsx').then(({ CreatorElements }) => ({
        default: CreatorElements,
    })),
);
const CreatorElements = withLoading(CreatorElementsPage);

const CreatePathwayPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/pathways/___pages___/create-pathway/page.tsx').then(
        ({ CreatePathway }) => ({
            default: CreatePathway,
        }),
    ),
);
const CreatePathway = withLoading(CreatePathwayPage);

const EditPathwayPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/pathways/___pages___/edit-pathway/page.tsx').then(
        ({ EditPathway }) => ({
            default: EditPathway,
        }),
    ),
);
const EditPathway = withLoading(EditPathwayPage);

const CreatorPathwaysPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/___pages___/pathways/page.tsx').then(({ CreatorPathways }) => ({
        default: CreatorPathways,
    })),
);
const CreatorPathways = withLoading(CreatorPathwaysPage);

const AdminCenterCreatorPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/creator/page.tsx').then(({ AdminCenterCreator }) => ({
        default: AdminCenterCreator,
    })),
);
const AdminCenterCreator = withLoading(AdminCenterCreatorPage);

const FleetVesselPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/fleet-dashboard/___pages___/fleet-vessel/page.tsx').then(
        ({ FleetVessel }) => ({
            default: FleetVessel,
        }),
    ),
);
const FleetVessel = withLoading(FleetVesselPage);

const FleetLearningDashboardPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/fleet-dashboard/___pages___/learning-dashboard/page.tsx').then(
        ({ FleetLearningDashboard }) => ({
            default: FleetLearningDashboard,
        }),
    ),
);
const FleetLearningDashboard = withLoading(FleetLearningDashboardPage);

const AdminCenterFleetDashboardPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/fleet-dashboard/page.tsx').then(({ AdminCenterFleetDashboard }) => ({
        default: AdminCenterFleetDashboard,
    })),
);
const AdminCenterFleetDashboard = withLoading(AdminCenterFleetDashboardPage);

const CreateOrEditLayoutPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/___shared___/create-edit-layout').then(
        ({ CreateOrEditLayout }) => ({
            default: CreateOrEditLayout,
        }),
    ),
);
const CreateOrEditLayout = withLoading(CreateOrEditLayoutPage);

const ClientInfoStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/page.tsx').then(
        ({ ClientInfoStep }) => ({
            default: ClientInfoStep,
        }),
    ),
);
const ClientInfoStep = withLoading(ClientInfoStepPage);

const ConsentStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/page.tsx').then(
        ({ ConsentStep }) => ({
            default: ConsentStep,
        }),
    ),
);
const ConsentStep = withLoading(ConsentStepPage);

const CrewUsersStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/page.tsx').then(
        ({ CrewUsersStep }) => ({
            default: CrewUsersStep,
        }),
    ),
);
const CrewUsersStep = withLoading(CrewUsersStepPage);

const PwSessionStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/page.tsx').then(
        ({ PwSessionStep }) => ({
            default: PwSessionStep,
        }),
    ),
);
const PwSessionStep = withLoading(PwSessionStepPage);

const ReferencesStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/page.tsx').then(
        ({ ReferencesStep }) => ({
            default: ReferencesStep,
        }),
    ),
);
const ReferencesStep = withLoading(ReferencesStepPage);

const ShoreUsersStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/page.tsx').then(
        ({ ShoreUsersStep }) => ({
            default: ShoreUsersStep,
        }),
    ),
);
const ShoreUsersStep = withLoading(ShoreUsersStepPage);

const TenantInfoStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/page.tsx').then(
        ({ TenantInfoStep }) => ({
            default: TenantInfoStep,
        }),
    ),
);
const TenantInfoStep = withLoading(TenantInfoStepPage);

const VesselsStepPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/vessels/page.tsx').then(
        ({ VesselsStep }) => ({
            default: VesselsStep,
        }),
    ),
);
const VesselsStep = withLoading(VesselsStepPage);

const ConfigurationLayoutPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/components/configuration-layout').then(
        ({ ConfigurationLayout }) => ({
            default: ConfigurationLayout,
        }),
    ),
);
const ConfigurationLayout = withLoading(ConfigurationLayoutPage);

const MatricesPageComponent = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/matrices/page.tsx').then(({ MatricesPage }) => ({
        default: MatricesPage,
    })),
);
const MatricesPage = withLoading(MatricesPageComponent);

const RolesPageComponent = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/roles/page.tsx').then(({ RolesPage }) => ({
        default: RolesPage,
    })),
);
const RolesPage = withLoading(RolesPageComponent);

const SubscriptionsPageComponent = React.lazy(() =>
    import('@routes/sidebar/admin-center/tenant-client/___pages___/subscriptions/page.tsx').then(
        ({ SubscriptionsPage }) => ({
            default: SubscriptionsPage,
        }),
    ),
);
const SubscriptionsPage = withLoading(SubscriptionsPageComponent);

const AdminCenterPage = React.lazy(() => import('@routes/sidebar/admin-center/tenant-client/page.tsx'));
const AdminCenter = withLoading(AdminCenterPage);

const TenantClientAdministrationPage = React.lazy(
    () => import('@routes/sidebar/admin-center/tenant-client/ui/tenant-client-administration-overview'),
);
const TenantClientAdministration = withLoading(TenantClientAdministrationPage);

const VesselLearningDashboardPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/vessel-dashboard/___pages___/learning-dashboard/page.tsx').then(
        ({ VesselLearningDashboard }) => ({
            default: VesselLearningDashboard,
        }),
    ),
);
const VesselLearningDashboard = withLoading(VesselLearningDashboardPage);

const AdminCenterVesselDashboardPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/vessel-dashboard/page.tsx').then(({ AdminCenterVesselDashboard }) => ({
        default: AdminCenterVesselDashboard,
    })),
);
const AdminCenterVesselDashboard = withLoading(AdminCenterVesselDashboardPage);

const AdminCenterAssessorPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/assessor/page.tsx').then(({ AdminCenterAssessor }) => ({
        default: AdminCenterAssessor,
    })),
);
const AdminCenterAssessor = withLoading(AdminCenterAssessorPage);
const AssessorTasksPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/assessor/___pages___/tasks/page.tsx').then(({ AssessorTasks }) => ({
        default: AssessorTasks,
    })),
);
const AssessorTasks = withLoading(AssessorTasksPage);
const AdminCenterAssessorEvaluateTaskPage = React.lazy(() =>
    import('@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/page.tsx').then(
        ({ AssessorEvaluateTask }) => ({
            default: AssessorEvaluateTask,
        }),
    ),
);
const AdminCenterAssessorEvaluateTask = withLoading(AdminCenterAssessorEvaluateTaskPage);

const PrintAllTasksPage = React.lazy(() =>
    import('@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-all-tasks').then(
        ({ PrintAllTasks }) => ({
            default: PrintAllTasks,
        }),
    ),
);
const PrintAllTasks = withLoading(PrintAllTasksPage);

const AppRouterContent = () => {
    const mockPage = <ServiceUnavailable />;

    const authLayout = (
        <SplitScreenLayout
            imageSide={<img src={getRandomAuthImage()} alt="auth illustration" />}
            contentSide={<Outlet />}
        />
    );

    const protectedMainLayout = (
        <RequireAuth>
            <MainLayout />
        </RequireAuth>
    );

    const protectedCustomMainLayout = (
        <RequireAuth>
            <MainLayout custom />
        </RequireAuth>
    );

    // admin pages intentionally remain unchanged for now — no layout adjustments on screens wider than 1920px.
    const protectedAdminMainLayout = (
        <RequireAuth>
            <MainLayout fullWidth />
        </RequireAuth>
    );

    // for pages where we don't want any layout, but a clear page (e.g. for printing)
    const protectedStandaloneLayout = (
        <RequireAuth>
            <Outlet />
        </RequireAuth>
    );

    return (
        <>
            <HealthCheckGuard />

            <Routes>
                <Route element={<Outlet />}>
                    <Route element={authLayout}>
                        <Route path={ROUTES.AUTH.LOGIN} element={<Login />} />
                        <Route path={ROUTES.AUTH.REGISTRATION} element={<Register />} />
                        <Route path={ROUTES.AUTH.RESET_PASSWORD} element={<PasswordChange />} />
                        <Route path={ROUTES.AUTH.RESET_PASSWORD_SUCCESS} element={<PasswordChangeSuccess />} />
                        <Route path={ROUTES.AUTH.TEMP_LOCKED_ACCOUNT} element={<AccountLocked />} />
                    </Route>

                    <Route element={protectedMainLayout}>
                        <Route index path={ROUTES.HOME} element={<Home />} />
                        <Route path={ROUTES.PROFILE_BAR.MY_PROFILE} element={<MyProfile />} />
                        <Route path={ROUTES.PROFILE_BAR.RESULTS} element={<MyResults />} />
                        <Route path={ROUTES.SIDEBAR.LEARNING} element={<MyLearning />} />
                        <Route path={ROUTES.SIDEBAR.SETTINGS} element={<UserSettings />} />
                        <Route path={ROUTES.PROFILE_BAR.USER_NOTIFICATIONS} element={<Notifications />} />
                        <Route path={ROUTES.SIDEBAR.ASSESSMENT} element={<h2>Assessment</h2>} />
                        <Route path={ROUTES.SIDEBAR.ASK} element={<h2>Ask</h2>} />
                        <Route path={ROUTES.SIDEBAR.PERFORM} element={<h2>Perform</h2>} />
                        <Route path={ROUTES.SIDEBAR.HELP_CENTER} element={<h2>Help Center</h2>} />
                    </Route>

                    <Route element={protectedAdminMainLayout}>
                        <Route path={ROUTES.SIDEBAR.ADMIN.ROOT} element={<AdminLandingGuard />}>
                            <Route element={<AdminCenter />}>
                                <Route
                                    index
                                    element={
                                        <Navigate
                                            to={`/${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.TENANT_CLIENT_ADMINISTRATION}`}
                                            replace
                                        />
                                    }
                                />
                                <Route
                                    path={ROUTES.SIDEBAR.ADMIN.TENANT_CLIENT_ADMINISTRATION}
                                    element={<TenantClientAdministration />}
                                />
                                <Route path={ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT} element={<AdminCenterAssessor />}>
                                    <Route index element={<AssessorTasks />} />
                                    <Route
                                        path={`${ROUTES.SIDEBAR.ADMIN.ASSESSOR.TASK}/:userPathwayId/:pathwayItemId`}
                                        element={<AdminCenterAssessorEvaluateTask />}
                                    />
                                </Route>
                                <Route path={ROUTES.SIDEBAR.ADMIN.CREATOR} element={<AdminCenterCreator />}>
                                    <Route index element={<Navigate to="elements" replace />} />
                                    <Route path="elements" element={<CreatorElements />} />
                                    <Route path="pathways" element={<CreatorPathways />} />
                                </Route>
                                <Route
                                    path={ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD}
                                    element={<AdminCenterFleetDashboard />}
                                >
                                    <Route index element={<Navigate to="learning-dashboard" replace />} />
                                    <Route path="learning-dashboard" element={<FleetLearningDashboard />} />
                                </Route>
                                <Route
                                    path={`${ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD}/${ROUTES.SIDEBAR.ADMIN.FLEET_VESSEL}/:fleetVesselId`}
                                    element={<FleetVessel />}
                                />
                                <Route
                                    path={`${ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD}/${ROUTES.SIDEBAR.ADMIN.FLEET_VESSEL}/:fleetVesselId/${ROUTES.SIDEBAR.ADMIN.VESSEL_CREW_MEMBER}/:vesselCrewMemberId`}
                                    element={<VesselCrewMember hasSelectedVessel />}
                                />

                                <Route
                                    path={ROUTES.SIDEBAR.ADMIN.VESSEL_DASHBOARD}
                                    element={<AdminCenterVesselDashboard />}
                                >
                                    <Route index element={<Navigate to="learning-dashboard" replace />} />
                                    <Route path="learning-dashboard" element={<VesselLearningDashboard />} />
                                </Route>
                                <Route
                                    path={`${ROUTES.SIDEBAR.ADMIN.VESSEL_DASHBOARD}/${ROUTES.SIDEBAR.ADMIN.VESSEL_CREW_MEMBER}/:vesselCrewMemberId`}
                                    element={<VesselCrewMember hasSelectedVessel={false} />}
                                />
                            </Route>

                            <Route
                                path={`${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.CREATE}`}
                                element={<CreatePathway />}
                            />
                            <Route
                                path={`${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.EDIT}`}
                                element={<EditPathway />}
                            />

                            <Route
                                path={`${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.CREATE}`}
                                element={<CreateElement />}
                            />
                            <Route
                                path={`${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.EDIT}`}
                                element={<EditElement />}
                            />

                            <Route element={<CreateOrEditLayout />}>
                                <Route path={ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT} element={<ConfigurationLayout />}>
                                    {/* CREATE CLIENT: no clientId */}
                                    <Route
                                        index
                                        element={
                                            <Navigate to={ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO} replace />
                                        }
                                    />
                                    <Route
                                        path={ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}
                                        element={
                                            <BlueByteAdminRoleGuard>
                                                <ClientInfoStep />
                                            </BlueByteAdminRoleGuard>
                                        }
                                    />

                                    {/* EDIT FLOW: clientId always present */}
                                    <Route path=":clientId" element={<Outlet />}>
                                        <Route
                                            index
                                            element={
                                                <Navigate to={ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO} replace />
                                            }
                                        />

                                        {/* client-info: tenantAlias optional */}
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}/:tenantAlias?`}
                                            element={<ClientInfoStep />}
                                        />

                                        {/* tenant-info: tenantAlias optional BUT clientId always (because parent route has it) */}
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.TENANT_INFO}/:tenantAlias?`}
                                            element={<TenantInfoStep />}
                                        />

                                        {/* the rest: tenantAlias REQUIRED */}
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.PW_SESSION}/:tenantAlias`}
                                            element={<PwSessionStep />}
                                        />
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.REFERENCES}/:tenantAlias`}
                                            element={<ReferencesStep />}
                                        />
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.SHORE_USERS}/:tenantAlias`}
                                            element={<ShoreUsersStep />}
                                        />
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.VESSELS}/:tenantAlias`}
                                            element={<VesselsStep />}
                                        />
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CREW_USERS}/:tenantAlias`}
                                            element={<CrewUsersStep />}
                                        />
                                        <Route
                                            path={`${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CONSENT}/:tenantAlias`}
                                            element={<ConsentStep />}
                                        />
                                    </Route>
                                </Route>

                                {/* other tabs already require both: keep as-is */}
                                <Route path="subscriptions/:clientId/:tenantAlias" element={<SubscriptionsPage />} />
                                <Route path="matrices/:clientId/:tenantAlias" element={<MatricesPage />} />
                                <Route path="roles/:clientId/:tenantAlias" element={<RolesPage />} />
                                <Route path="analytics/:clientId/:tenantAlias" element={<h2>Analytics</h2>} />
                                <Route path="templates/:clientId/:tenantAlias" element={<h2>Templates</h2>} />
                            </Route>
                        </Route>
                    </Route>

                    <Route element={protectedCustomMainLayout}>
                        <Route path={`${ROUTES.SIDEBAR.LEARNING}/:id`} element={<LearningOverview />} />
                    </Route>
                    <Route element={protectedStandaloneLayout}>
                        <Route
                            path={`${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.CERTIFICATE_PREVIEW}`}
                            element={<CertificatePreview />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.LEARNING}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT}/:pathwayId/:taskId`}
                            element={<PrintTask isAssessorView={false} />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT}/:pathwayId/:taskId`}
                            element={<PrintTask isAssessorView />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.LEARNING}/task/${ROUTES.SIDEBAR.LEARNING_TASK.EVALUATION_LOG}/:pathwayId/:taskId`}
                            element={<TaskEvaluationLog isAssessorView={false} />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT}/task/${ROUTES.SIDEBAR.LEARNING_TASK.EVALUATION_LOG}/:pathwayId/:taskId`}
                            element={<TaskEvaluationLog isAssessorView />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.LEARNING}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT_EVALUATION_LOG}/:pathwayId/:taskId`}
                            element={<PrintEvaluationLog isAssessorView={false} />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT_EVALUATION_LOG}/:pathwayId/:taskId`}
                            element={<PrintEvaluationLog isAssessorView />}
                        />
                        <Route
                            path={`${ROUTES.SIDEBAR.LEARNING}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT_ALL}/:pathwayId`}
                            element={<PrintAllTasks />}
                        />
                    </Route>
                </Route>
                <Route path={ROUTES.DEFAULT.SERVER_ERROR} element={<InternalServerError />} />
                <Route path={ROUTES.DEFAULT.CLIENT_ERROR} element={mockPage} />
                <Route path={ROUTES.DEFAULT.UNAUTHORIZED} element={<NotAuthorized />} />
                <Route path={ROUTES.DEFAULT.MAINTENANCE} element={<ServiceUnavailable />} />
                <Route path={ROUTES.DEFAULT.NO_PAGE} element={<NoSuchPage />} />
                <Route path={ROUTES.DEFAULT.NOT_FOUND} element={<NoClient />} />
            </Routes>
        </>
    );
};

const WrappedAppRouterContent = withGlobalAlertBanner(AppRouterContent);

export const AppRouter = () => (
    <BrowserRouter>
        <WrappedAppRouterContent />
    </BrowserRouter>
);
